import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '@/components/Auth/AuthProvider';

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, openAuthModal } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 28px',
          background: 'var(--bg-card)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid transparent',
          borderImage: 'linear-gradient(to right, transparent, var(--border-medium), transparent) 1',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 24px rgba(0,0,0,0.03)',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
          }}
        >
          <img
            src="/favicon.svg"
            alt="Pastewala"
            width={32}
            height={32}
            style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.35)',
            }}
          />
          <span
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '-0.3px',
            }}
          >
            <span style={{ color: '#FF8C42' }}>Paste</span>wala
          </span>
        </Link>

        {/* Hamburger button — visible only on mobile via CSS */}
        <button
          className="hamburger"
          type="button"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(v => !v)}
          style={{
            display: 'none', // overridden by CSS media query
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            background: 'transparent',
            border: '1px solid var(--border-medium)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
            transition: 'all 0.15s ease',
          }}
        >
          {menuOpen ? '✕' : '☰'}
        </button>

        {/* Right actions — nav-links class for mobile toggle */}
        <div className={`nav-links${menuOpen ? ' menu-open' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link
            to="/diff"
            onClick={() => setMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '7px 14px',
              background: 'transparent',
              border: '1px solid var(--border-medium)',
              borderRadius: '16px',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,140,66,0.07)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,140,66,0.3)';
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border-medium)';
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-secondary)';
            }}
          >
            Diff
          </Link>

          <Link
            to="/blog"
            onClick={() => setMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '7px 14px',
              background: 'transparent',
              border: '1px solid var(--border-medium)',
              borderRadius: '16px',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,140,66,0.07)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,140,66,0.3)';
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--accent-primary)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border-medium)';
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-secondary)';
            }}
          >
            Blog
          </Link>

          <ThemeToggle />

          {/* Auth area */}
          {user ? (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(v => !v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 14px',
                  background: 'transparent',
                  border: '1px solid var(--border-medium)',
                  borderRadius: '20px',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,140,66,0.4)';
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,140,66,0.06)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-medium)';
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {/* Avatar circle */}
                <div
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF8C42, #FF6B35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                </div>
                {user.username}
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>▼</span>
              </button>

              {/* Dropdown */}
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '160px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                    zIndex: 200,
                  }}
                >
                  <Link
                    to="/dashboard"
                    onClick={() => { setShowUserMenu(false); setMenuOpen(false); }}
                    style={{
                      display: 'block',
                      padding: '12px 16px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      textDecoration: 'none',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLAnchorElement).style.background = 'var(--bg-secondary)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                    }}
                  >
                    My Pastes
                  </Link>
                  {user.is_admin && (
                    <>
                      <div style={{ height: '1px', background: 'var(--border-light)' }} />
                      <Link
                        to="/admin"
                        onClick={() => { setShowUserMenu(false); setMenuOpen(false); }}
                        style={{
                          display: 'block',
                          padding: '12px 16px',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--accent-primary)',
                          textDecoration: 'none',
                          transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,140,66,0.07)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                        }}
                      >
                        Admin Panel
                      </Link>
                    </>
                  )}
                  <div style={{ height: '1px', background: 'var(--border-light)' }} />
                  <button
                    onClick={() => { logout(); setShowUserMenu(false); setMenuOpen(false); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#ef4444',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.07)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => { openAuthModal('login'); setMenuOpen(false); }}
              style={{
                padding: '7px 16px',
                background: 'transparent',
                border: '1px solid var(--border-medium)',
                borderRadius: '20px',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,140,66,0.4)';
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,140,66,0.06)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-medium)';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              Sign In
            </button>
          )}

          <button
            onClick={() => { navigate('/'); setMenuOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '9px 20px',
              background: 'linear-gradient(135deg, #FF8C42, #FF6B35)',
              border: 'none',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(255, 107, 53, 0.3)',
              transition: 'all 0.2s ease',
              letterSpacing: '-0.1px',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px) scale(1.03)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.45)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = '';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 10px rgba(255, 107, 53, 0.3)';
            }}
          >
            <span style={{ fontSize: '16px' }}>+</span>
            New Paste
          </button>
        </div>
      </nav>
    </>
  );
}
